import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { pdf } from '@react-pdf/renderer';
import {
  FaArrowLeft, FaMapMarkerAlt, FaCheckCircle, FaTimes, FaSpinner, FaFilePdf,
  FaWeightHanging, FaTruck, FaBoxOpen, FaClipboardList, FaCalendarAlt, FaClock,
  FaFileContract, FaUserTie, FaGlobeAfrica, FaUpload
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import Modal from '../components/Modal';
import QuotePDF from '../components/QuotePDF';
import { getCompanyDetails } from '../config/CompanyDetails';
import { clientQuotes } from '../api';
import { API_URL } from '../config';

// Keep these in step with ClientQuotesPage.js
const STATUS_META = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9' },
  client_request: { label: 'Pending Review', color: '#b45309', bg: '#fffbeb' },
  sent: { label: 'Sent for Review', color: '#2563eb', bg: '#eff6ff' },
  client_accepted: { label: 'Accepted', color: '#16a34a', bg: '#f0fdf4' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' },
  expired: { label: 'Expired', color: '#dc2626', bg: '#fef2f2' },
  credited: { label: 'Credited', color: '#6b7280', bg: '#f3f4f6' },
  // Voided = the invoice was cancelled before any payment was received. Never "Credited":
  // nothing came in, so nothing is owed back.
  void: { label: 'Voided', color: '#6b7280', bg: '#f3f4f6' }
};

const TYPE_LABELS = {
  minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal',
  container: '📦 Container', general: '📋 General'
};

const TERM_LABELS = {
  standard: 'Standard Terms & Conditions',
  abnormal: 'Abnormal Load Transport Terms',
  enm: 'ENM Terms & Conditions',
  lovemore: 'Lovemore Terms & Conditions',
  benecke: 'Benecke Terms & Conditions',
  transcor: 'Transcor Terms & Conditions'
};

const TRUCK_LABELS = {
  side_tipper: 'Side Tipper', flat_deck: 'Flat Deck', lowbed: 'Lowbed',
  tautliner: 'Tautliner', grain_side_tipper: 'Grain Side Tipper',
  extended_trailer: 'Extended Trailer', step_deck: 'Step Deck', other: 'Other'
};

// A client may download the PDF once we have issued the quote (sent) and after
// accepting it — never while it is still a draft or once it is rejected.
const DOWNLOADABLE_STATUSES = ['sent', 'client_accepted', 'approved'];
const canDownloadPdf = (q) => !!q && DOWNLOADABLE_STATUSES.includes(q.status);

const fmtMoney = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const titleize = (v) => v ? String(v).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null;

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5" style={{ color: '#94A3B8' }}>{icon}</span>
    <div>
      <dt className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>{label}</dt>
      <dd style={{ color: '#0A1929' }}>{value}</dd>
    </div>
  </div>
);

const Field = ({ icon, label, value }) => (
  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
    <p className="text-xs font-semibold uppercase mb-1 flex items-center gap-1.5" style={{ color: '#94A3B8' }}>
      {icon} {label}
    </p>
    <p style={{ color: '#0A1929' }}>{value}</p>
  </div>
);

function ClientQuoteDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  // Accepting now requires the Terms & Conditions tick; a purchase order is optional.
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [poFile, setPoFile] = useState(null);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await clientQuotes.getOne(id);
        if (cancelled) return;
        if (res.success && res.quote) setQuote(res.quote);
        else setError(res.error || 'This quote could not be found.');
      } catch (err) {
        if (!cancelled) setError('Could not load this quote. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const handleDownload = async () => {
    if (!canDownloadPdf(quote) || downloading) return;
    setDownloading(true);
    try {
      const company = await getCompanyDetails();
      const blob = await pdf(<QuotePDF quote={quote} companyDetails={company} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quote.quote_number || 'Quote'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('✅ Quote PDF downloaded');
    } catch (err) {
      console.error('Quote PDF error:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const confirmAccept = async () => {
    if (!termsAgreed) { toast.error('Please accept the Terms and Conditions to continue'); return; }
    setBusy(true);
    try {
      // Optional purchase order — uploaded once, then carried onto the job + invoice.
      let poUrl = null;
      let poName = null;
      if (poFile) {
        setUploadingPo(true);
        const fd = new FormData();
        fd.append('image', poFile);
        const ur = await fetch(`${API_URL}/uploads`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        });
        const ud = await ur.json();
        if (!ud.success) throw new Error(ud.error || 'Could not upload the purchase order');
        poUrl = ud.url || ud.file_url || null;
        poName = poFile.name;
        setUploadingPo(false);
      }
      const res = await clientQuotes.accept(quote.id, {
        terms_accepted: true,
        purchase_order_number: poNumber.trim() || null,
        purchase_order_url: poUrl,
        purchase_order_name: poName,
      });
      if (res.success) {
        toast.success(`✅ Quote ${quote.quote_number} accepted. We'll confirm your job shortly.`);
        setAcceptOpen(false);
        setTermsAgreed(false);
        setPoNumber('');
        setPoFile(null);
        setQuote((prev) => ({ ...prev, ...(res.quote || {}), status: 'client_accepted' }));
      } else {
        toast.error(res.error || 'Failed to accept quote');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to accept quote');
    } finally {
      setBusy(false);
      setUploadingPo(false);
    }
  };

  const confirmReject = async () => {
    setBusy(true);
    try {
      const res = await clientQuotes.reject(quote.id, { reason });
      if (res.success) {
        toast.success(`Quote ${quote.quote_number} rejected.`);
        setRejectOpen(false);
        setReason('');
        setQuote((prev) => ({ ...prev, ...(res.quote || {}), status: 'rejected', rejection_reason: reason || prev.rejection_reason }));
      } else {
        toast.error(res.error || 'Failed to reject quote');
      }
    } catch {
      toast.error('Failed to reject quote');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading quote...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error || !quote) {
    return (
      <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <button onClick={() => navigate('/client-quotes')} className="flex items-center gap-2 text-sm mb-6 hover:underline" style={{ color: '#00A86B' }}>
          <FaArrowLeft size={12} /> Back to My Quotes
        </button>
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-5xl mb-4">📄</div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>Quote unavailable</h3>
          <p className="text-sm text-gray-500">{error || 'This quote could not be found.'}</p>
        </div>
      </ClientLayout>
    );
  }

  // A quote past its expiry date shows as Expired straight away (the backend flips the
  // real status too — this just makes it instant and stops it being accepted).
  const effectiveStatus = (quote.status === 'sent' || quote.status === 'client_request')
    && quote.expiry_date && new Date(quote.expiry_date) < new Date(new Date().toDateString())
    ? 'expired'
    : quote.status;
  const meta = STATUS_META[effectiveStatus] || STATUS_META.draft;
  const items = quote.items || [];
  const legs = quote.legs || [];
  const subtotal = quote.total_amount != null ? Number(quote.total_amount) : items.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const vat = Number(quote.vat_amount) || 0;
  const grand = quote.grand_total != null ? Number(quote.grand_total) : subtotal + vat;
  const termLabels = (() => {
    const raw = quote.terms_conditions;
    const keys = !raw ? ['standard'] : (Array.isArray(raw) ? raw : String(raw).split(',').map((t) => t.trim()).filter(Boolean));
    // Always read as a plain "Terms and Conditions" — never name the client's own
    // term set (e.g. "Benecke Terms & Conditions"), and only once even when several
    // term flags are set. Anything unrecognised shows exactly as stored.
    const labels = keys.map((k) => (TERM_LABELS[k] ? 'Terms and Conditions' : k));
    return Array.from(new Set(labels));
  })();
  const truckLabel = quote.truck_type ? (TRUCK_LABELS[quote.truck_type] || titleize(quote.truck_type)) : null;
  const downloadable = canDownloadPdf(quote);
  const isSent = quote.status === 'sent' && effectiveStatus !== 'expired';

  return (
    <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
      {/* ── Back ── */}
      <button onClick={() => navigate('/client-quotes')} className="flex items-center gap-2 text-sm mb-5 hover:underline" style={{ color: '#00A86B' }}>
        <FaArrowLeft size={12} /> Back to My Quotes
      </button>

      {/* ── Header ── */}
      <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>{quote.quote_number}</h1>
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
              {quote.cross_border && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5" style={{ color: '#b45309', backgroundColor: '#fffbeb' }}>
                  <FaGlobeAfrica size={11} /> Cross-border
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: '#4A5568' }}>
              {TYPE_LABELS[quote.quote_type] || titleize(quote.quote_type)}
              {quote.created_at ? ` • Issued ${fmtDate(quote.created_at)}` : ''}
              {quote.expiry_date ? ` • Valid until ${fmtDate(quote.expiry_date)}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {downloadable && (
              <button onClick={handleDownload} disabled={downloading} className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50 disabled:opacity-50" style={{ borderColor: '#E8ECEF', color: '#0A1929' }}>
                {downloading ? <FaSpinner className="animate-spin" size={13} /> : <FaFilePdf size={13} />}
                {downloading ? 'Generating...' : 'Download PDF'}
              </button>
            )}
            {isSent && (
              <>
                <button onClick={() => setRejectOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50">
                  <FaTimes size={13} /> Reject
                </button>
                <button onClick={() => setAcceptOpen(true)} className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#00A86B' }}>
                  <FaCheckCircle size={14} /> Accept Quote
                </button>
              </>
            )}
          </div>
        </div>

        {quote.status === 'draft' && (
          <p className="text-xs mt-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
            This quote is still being prepared, so the PDF is not available yet. You will be able to download it as soon as we send it to you.
          </p>
        )}
        {effectiveStatus === 'expired' && (
          <div className="mt-4 px-3 py-2 rounded-lg flex flex-wrap items-center justify-between gap-2" style={{ backgroundColor: '#fef2f2' }}>
            <p className="text-xs" style={{ color: '#b91c1c' }}>
              This quote expired on {fmtDate(quote.expiry_date)} and can no longer be accepted.
            </p>
            <button
              onClick={() => navigate('/client-quotes')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shrink-0"
              style={{ backgroundColor: '#00A86B' }}
            >
              Request an updated quote
            </button>
          </div>
        )}
        {quote.status === 'rejected' && (
          <p className="text-xs mt-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
            You rejected this quote{quote.rejection_reason ? `: ${quote.rejection_reason}` : '.'}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* ── Route & distance ── */}
        <div className="lg:col-span-2 bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaMapMarkerAlt size={14} style={{ color: '#00A86B' }} /> Route and Distance
          </h2>

          {legs.length > 0 ? (
            <div className="space-y-4">
              {legs.map((leg, idx) => (
                <div key={leg.id || idx} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: '#0A1929' }}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                      <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#00A86B' }}>Pick-up</p>
                      <p className="text-sm" style={{ color: '#0A1929' }}>{leg.pickup_address || '—'}</p>
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                      <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#2563eb' }}>Delivery</p>
                      <p className="text-sm" style={{ color: '#0A1929' }}>{leg.delivery_address || '—'}</p>
                    </div>
                    {leg.distance_km ? (
                      <p className="text-xs sm:col-span-2" style={{ color: '#4A5568' }}>
                        Leg distance: <strong>{leg.distance_km} km</strong>
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: '#f8fafc', color: '#4A5568' }}>
              {quote.origin || quote.destination
                ? `${quote.origin || '—'} → ${quote.destination || '—'}`
                : 'Route to be confirmed.'}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 mt-5 pt-4 border-t text-sm" style={{ borderColor: '#E8ECEF', color: '#4A5568' }}>
            {quote.distance_km ? (
              <span>Total distance: <strong style={{ color: '#0A1929' }}>{quote.distance_km} km</strong></span>
            ) : null}
            {quote.cross_border ? (
              <span className="flex items-center gap-1.5" style={{ color: '#b45309' }}>
                <FaGlobeAfrica size={12} />
                Cross-border{quote.origin_country && quote.destination_country ? ` (${quote.origin_country} to ${quote.destination_country})` : ''}
              </span>
            ) : (
              <span>Domestic route</span>
            )}
          </div>
        </div>

        {/* ── Quote summary ── */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-semibold mb-4" style={{ color: '#0A1929' }}>Quote Details</h2>
          <dl className="space-y-4 text-sm">
            <DetailRow icon={<FaCalendarAlt size={13} />} label="Issued" value={fmtDate(quote.created_at)} />
            <DetailRow icon={<FaClock size={13} />} label="Valid until" value={fmtDate(quote.expiry_date)} />
            <DetailRow icon={<FaFileContract size={13} />} label="Payment terms" value={quote.payment_terms || '—'} />
            <DetailRow icon={<FaMapMarkerAlt size={13} />} label="Distance" value={quote.distance_km ? `${quote.distance_km} km` : '—'} />
            <DetailRow
              icon={<FaUserTie size={13} />}
              label="Prepared by"
              value={quote.salesperson?.full_name || quote.salesperson?.email || 'One Purpose Logistics'}
            />
          </dl>
        </div>
      </div>

      {(quote.commodity || quote.tonnage || truckLabel || quote.wet_or_dry || quote.description || quote.services) && (
        <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaBoxOpen size={14} style={{ color: '#00A86B' }} /> What We Are Moving
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            {quote.commodity ? <Field icon={<FaBoxOpen size={11} />} label="Commodity" value={quote.commodity} /> : null}
            {quote.tonnage ? <Field icon={<FaWeightHanging size={11} />} label="Tonnage" value={`${quote.tonnage} MT`} /> : null}
            {truckLabel ? <Field icon={<FaTruck size={11} />} label="Truck type" value={truckLabel} /> : null}
            {quote.wet_or_dry ? <Field icon={<FaClipboardList size={11} />} label="Condition" value={titleize(quote.wet_or_dry)} /> : null}
          </div>
          {quote.description && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#94A3B8' }}>Description</p>
              <p className="text-sm whitespace-pre-line" style={{ color: '#0A1929' }}>{quote.description}</p>
            </div>
          )}
          {quote.services && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#94A3B8' }}>Services included</p>
              <p className="text-sm whitespace-pre-line" style={{ color: '#0A1929' }}>{quote.services}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Line items + totals ── */}
      <div className="bg-white rounded-xl border overflow-hidden mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="px-6 py-4 border-b" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-semibold flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaClipboardList size={14} style={{ color: '#00A86B' }} /> Pricing Breakdown
          </h2>
        </div>

        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Unit price</th>
                  <th className="px-6 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id || i} className="border-t" style={{ borderColor: '#F1F5F9' }}>
                    <td className="px-6 py-3" style={{ color: '#0A1929' }}>
                      {it.description || '—'}
                      {it.unit ? <span className="text-gray-400 text-xs"> per {titleize(it.unit)}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{it.quantity}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{fmtMoney(it.unit_price)}</td>
                    <td className="px-6 py-3 text-right font-medium" style={{ color: '#0A1929' }}>{fmtMoney(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-6 py-4 text-sm text-gray-500">The pricing lines for this quote are still being finalised.</p>
        )}

        <div className="px-6 py-4 border-t flex flex-col items-end gap-1 text-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex justify-between w-full max-w-xs text-gray-600">
            <span>Subtotal (excl. VAT)</span>
            <span>{fmtMoney(subtotal)}</span>
          </div>
          <div className="flex justify-between w-full max-w-xs text-gray-600">
            <span>VAT</span>
            <span>{fmtMoney(vat)}</span>
          </div>
          <div className="flex justify-between w-full max-w-xs font-bold text-base pt-2 border-t" style={{ color: '#0A1929', borderColor: '#E8ECEF' }}>
            <span>Total</span>
            <span>{fmtMoney(grand)}</span>
          </div>
          {vat === 0 && (
            <p className="text-xs text-gray-500 mt-2 text-right max-w-xs">
              No VAT is charged on this quote{quote.cross_border ? ' (cross-border transport)' : ''}.
            </p>
          )}
        </div>
      </div>

      {/* ── Terms & conditions ── */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaFileContract size={14} style={{ color: '#00A86B' }} /> Terms and Conditions
        </h2>
        <ul className="space-y-2 text-sm" style={{ color: '#4A5568' }}>
          {termLabels.map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <FaCheckCircle size={11} className="mt-1 flex-shrink-0" style={{ color: '#00A86B' }} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        {quote.custom_terms && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#94A3B8' }}>Additional terms</p>
            <p className="text-sm whitespace-pre-line" style={{ color: '#0A1929' }}>{quote.custom_terms}</p>
          </div>
        )}
        <p className="text-xs mt-4" style={{ color: '#94A3B8' }}>
          The full wording of these terms is included in the quote PDF. If anything needs clarifying, reply to your quote email and your account manager will assist.
        </p>
      </div>




      {/* ── Accept confirm ── */}
      <Modal isOpen={acceptOpen} onClose={() => { if (!busy) setAcceptOpen(false); }} title="Accept Quote">
        <p className="text-gray-600 text-sm mb-4">
          Accept quote <strong>{quote.quote_number}</strong>? Our team will confirm your job shortly after you accept.
        </p>
        <div className="space-y-4 mb-6">
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={termsAgreed} onChange={(e) => setTermsAgreed(e.target.checked)} className="mt-1" />
            <span>
              I have read and accept the <strong>Terms and Conditions</strong> for this quote
              {quote.custom_terms ? ', including the additional terms' : ''}. <span className="text-red-600">*</span>
            </span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase order number <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. PO-12345" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attach your purchase order <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input type="file" accept=".pdf,image/*" onChange={(e) => setPoFile(e.target.files?.[0] || null)} className="block w-full text-sm text-gray-600" />
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
              <FaUpload size={10} /> PDF or photo is fine — it's attached to your quote and carried onto your job and invoice.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setAcceptOpen(false)} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={confirmAccept} disabled={busy || !termsAgreed} className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#00A86B' }}>
            {busy || uploadingPo ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} {uploadingPo ? 'Uploading…' : 'Accept'}
          </button>
        </div>
      </Modal>

      {/* ── Reject ── */}
      <Modal isOpen={rejectOpen} onClose={() => { if (!busy) { setRejectOpen(false); setReason(''); } }} title="Reject Quote">
        <p className="text-gray-600 text-sm mb-4">
          Reject quote <strong>{quote.quote_number}</strong>? Please let us know why so we can improve (optional).
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (optional)..."
          className="w-full px-3 py-2 border rounded-lg text-sm mb-6 focus:outline-none"
          style={{ borderColor: '#E8ECEF' }}
        />
        <div className="flex justify-end gap-3">
          <button onClick={() => { setRejectOpen(false); setReason(''); }} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={confirmReject} disabled={busy} className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#dc2626' }}>
            {busy ? <FaSpinner className="animate-spin" /> : <FaTimes />} Reject
          </button>
        </div>
      </Modal>

    </ClientLayout>
  );
}

export default ClientQuoteDetail;

